import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { toast } from "sonner";

const DOC_TITLES = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  refund: "Refund & Cancellation Policy",
  prohibited: "Prohibited Items Policy",
  protection: "CarryIt Protection",
  carrier_agreement: "Carrier Agreement",
  grievance: "Grievance Redressal",
};

const DOC_SLUGS = {
  terms: "/terms",
  privacy: "/privacy",
  refund: "/refund-policy",
  prohibited: "/prohibited-items",
  protection: "/protection",
  carrier_agreement: "/carrier-agreement",
  grievance: "/grievance",
};

/**
 * Blocking modal for existing users who haven't accepted the current
 * ToS + Privacy version (or any other doc listed in `outstanding_required`).
 */
const LegalGate = () => {
  const { user } = useAuth();
  const [state, setState] = useState({ versions: {}, accepted: {}, outstanding: [] });
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/legal/my-acceptances");
      setState({
        versions: r.data.versions || {},
        accepted: r.data.accepted || {},
        outstanding: r.data.outstanding_required || [],
      });
    } catch {
      // Silent — non-authenticated calls or transient errors shouldn't block.
    }
  };

  useEffect(() => { if (user) refresh(); }, [user?.id]);

  if (!user || state.outstanding.length === 0) return null;

  const acceptAll = async () => {
    setBusy(true);
    try {
      for (const doc of state.outstanding) {
        await api.post("/legal/accept", { doc, version: state.versions[doc] });
      }
      await refresh();
      toast.success("Thanks — you're all set.");
      setChecked(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record your acceptance");
    }
    setBusy(false);
  };

  return (
    <Dialog open={true}>
      <DialogContent className="max-w-md" data-testid="legal-gate-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-amber-100 grid place-items-center"><FileText className="w-4 h-4 text-amber-700"/></div>
            <DialogTitle>We've updated our policies</DialogTitle>
          </div>
          <DialogDescription>
            Please review and accept the following before you continue using CarryIt.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {state.outstanding.map((doc) => (
            <Link
              key={doc}
              to={DOC_SLUGS[doc]}
              target="_blank"
              className="block rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2 text-sm"
              data-testid={`gate-doc-${doc}`}
            >
              <div className="font-medium text-slate-900">{DOC_TITLES[doc]}</div>
              <div className="text-[11px] text-slate-500">Version {state.versions[doc]} · Tap to read</div>
            </Link>
          ))}
        </div>
        <label className="flex items-start gap-2 pt-2 cursor-pointer select-none" data-testid="gate-accept-label">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            data-testid="gate-accept-checkbox"
            className="mt-0.5"
          />
          <span className="text-xs text-slate-600 leading-snug">
            I have read and accept the documents listed above.
          </span>
        </label>
        <Button
          data-testid="gate-accept-btn"
          disabled={!checked || busy}
          onClick={acceptAll}
          className="w-full rounded-full bg-slate-900 hover:bg-slate-800"
        >
          {busy ? "Saving…" : "Accept and continue"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default LegalGate;
